/**
 * Cell-type showcase — each column deliberately uses a different cell renderer
 * pattern so the table doubles as a reference for common cell types:
 *   Link · Colored pill · Currency (with null) · Multi-tag · Mailto link ·
 *   Status dot indicator · Relative date
 */
import React from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'

// ─── Types ───────────────────────────────────────────────────────────────────

type Stage =
  | 'Prospecting'
  | 'Qualified'
  | 'Proposal'
  | 'Negotiation'
  | 'Closed Won'
  | 'Closed Lost'

type Status = 'active' | 'won' | 'lost'

type Row = {
  name: string
  company: string
  stage: Stage
  revenue: number | null
  tags: string[]
  email: string
  lastContact: string
  status: Status
}

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const STAGE_COLORS: Record<Stage, { bg: string; text: string }> = {
  Prospecting: { bg: '#EFF6FF', text: '#1D4ED8' },
  Qualified: { bg: '#F5F3FF', text: '#6D28D9' },
  Proposal: { bg: '#FFFBEB', text: '#B45309' },
  Negotiation: { bg: '#FFF7ED', text: '#C2410C' },
  'Closed Won': { bg: '#F0FDF4', text: '#15803D' },
  'Closed Lost': { bg: '#FFF1F2', text: '#BE123C' },
}

const STATUS_DOT: Record<Status, string> = {
  active: '#16A34A',
  won: '#0EA5E9',
  lost: '#DC2626',
}

function StagePillCell({ stage }: { stage: Stage }) {
  const { bg, text } = STAGE_COLORS[stage]
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: bg, color: text }}
    >
      {stage}
    </span>
  )
}

function StatusCell({ status }: { status: Status }) {
  const labels: Record<Status, string> = {
    active: 'Active',
    won: 'Closed Won',
    lost: 'Lost',
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-fg">
      <span
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: STATUS_DOT[status] }}
      />
      {labels[status]}
    </span>
  )
}

function TagsCell({ tags }: { tags: string[] }) {
  const visible = tags.slice(0, 2)
  const extra = tags.length - visible.length
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {visible.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center px-1.5 py-0.5 rounded text-xxs bg-surface-alt text-fg-secondary font-medium"
        >
          {tag}
        </span>
      ))}
      {extra > 0 && <span className="text-xxs text-fg-muted">+{extra}</span>}
    </div>
  )
}

// ─── Column definitions ───────────────────────────────────────────────────────

const columns: ColumnDef<Row, any>[] = [
  {
    header: 'Contact',
    accessorKey: 'name',
    size: 150,
    // Link cell — clickable entity name
    cell: (info) => (
      <a href="#" className="text-brand hover:underline text-xs font-medium">
        {info.getValue()}
      </a>
    ),
  },
  {
    header: 'Company',
    accessorKey: 'company',
    size: 160,
    // Plain text — no renderer
  },
  {
    header: 'Stage',
    accessorKey: 'stage',
    size: 130,
    // Colored pill — bg/text color keyed off value
    cell: (info) => <StagePillCell stage={info.getValue()} />,
  },
  {
    header: 'Est. Revenue',
    accessorKey: 'revenue',
    size: 120,
    // Currency with null fallback — never show $0 for missing data
    cell: (info) => {
      const v: number | null = info.getValue()
      return v != null ? `$${v.toLocaleString()}` : '-'
    },
  },
  {
    header: 'Tags',
    accessorKey: 'tags',
    size: 180,
    // Multi-item pill list with "+N more" overflow
    cell: (info) => <TagsCell tags={info.getValue()} />,
  },
  {
    header: 'Email',
    accessorKey: 'email',
    size: 200,
    // Mailto link
    cell: (info) => (
      <a
        href={`mailto:${info.getValue()}`}
        className="text-xs text-brand hover:underline"
      >
        {info.getValue()}
      </a>
    ),
  },
  {
    header: 'Status',
    accessorKey: 'status',
    size: 110,
    // Status dot indicator
    cell: (info) => <StatusCell status={info.getValue()} />,
  },
  {
    header: 'Last Contact',
    accessorKey: 'lastContact',
    size: 120,
    // Relative date — plain string here, real tables use dayjs/date-fns
  },
]

// ─── Mock data ────────────────────────────────────────────────────────────────

const mockData: Row[] = [
  {
    name: 'Sarah Chen',
    company: 'Harvest Table',
    stage: 'Proposal',
    revenue: 84000,
    tags: ['Enterprise', 'Food & Bev'],
    email: 'sarah@harvesttable.com',
    lastContact: '2 days ago',
    status: 'active',
  },
  {
    name: 'Marcus Webb',
    company: 'Summit Hotels',
    stage: 'Qualified',
    revenue: 210000,
    tags: ['Hotels', 'High Priority', 'Multi-Unit'],
    email: 'mwebb@summithotels.com',
    lastContact: '1 week ago',
    status: 'active',
  },
  {
    name: 'Priya Nair',
    company: 'Oakwood Dining',
    stage: 'Closed Won',
    revenue: 56000,
    tags: ['Restaurant'],
    email: 'priya@oakwood.com',
    lastContact: '3 days ago',
    status: 'won',
  },
  {
    name: 'James Holloway',
    company: 'Blue Harbor Cafe',
    stage: 'Prospecting',
    revenue: 18000,
    tags: ['Cafe', 'SMB'],
    email: 'james@blueharbor.com',
    lastContact: 'Today',
    status: 'active',
  },
  {
    name: 'Aisha Powell',
    company: 'Crestline Foods',
    stage: 'Negotiation',
    revenue: 340000,
    tags: ['Food Service', 'Enterprise', 'Key Account'],
    email: 'a.powell@crestline.com',
    lastContact: 'Yesterday',
    status: 'active',
  },
  {
    name: 'Tom Eriksson',
    company: 'Nordic Kitchen',
    stage: 'Closed Lost',
    revenue: null,
    tags: ['Restaurant'],
    email: 'tom@nordickitchen.com',
    lastContact: '3 weeks ago',
    status: 'lost',
  },
  {
    name: 'Diana Marsh',
    company: 'Evergreen Hotels',
    stage: 'Proposal',
    revenue: 128000,
    tags: ['Hotels', 'High Priority'],
    email: 'dmarsh@evergreenhotels.com',
    lastContact: '4 days ago',
    status: 'active',
  },
  {
    name: 'Kevin Lim',
    company: 'Sunrise Catering',
    stage: 'Qualified',
    revenue: 95000,
    tags: ['Catering', 'SMB'],
    email: 'klim@sunrisecatering.com',
    lastContact: '1 week ago',
    status: 'active',
  },
  {
    name: 'Natalie Cross',
    company: 'Mesa Grille Group',
    stage: 'Closed Won',
    revenue: 47000,
    tags: ['Restaurant', 'Multi-Unit'],
    email: 'natalie@mesagrille.com',
    lastContact: '5 days ago',
    status: 'won',
  },
  {
    name: 'Omar Hassan',
    company: 'Pinnacle Resorts',
    stage: 'Prospecting',
    revenue: 260000,
    tags: ['Hotels', 'Enterprise', 'High Priority'],
    email: 'omar.h@pinnacle.com',
    lastContact: 'Today',
    status: 'active',
  },
  {
    name: 'Lisa Tran',
    company: 'Coastal Bistros',
    stage: 'Negotiation',
    revenue: 112000,
    tags: ['Restaurant', 'SMB'],
    email: 'lisa@coastalbistros.com',
    lastContact: '2 days ago',
    status: 'active',
  },
  {
    name: 'Ryan Park',
    company: 'Urban Eats Co.',
    stage: 'Prospecting',
    revenue: null,
    tags: ['Food & Bev'],
    email: 'rpark@urbaneats.com',
    lastContact: '6 days ago',
    status: 'active',
  },
]

// ─── Mock component ───────────────────────────────────────────────────────────

export function ClientDataTableMock(): React.ReactElement {
  return (
    <div className="w-full">
      <ClientDataTable
        columns={columns}
        data={mockData}
        enableSorting
        enableGlobalSearch
        searchPlaceholder="Search contacts..."
        smallRows
        height={400}
      />
    </div>
  )
}
